import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Row, Col, InputGroup, Form, PageHeader } from '@/components/ui';
import { Asset } from '../../../types/Asset';
import { assetService, PaginatedAssetsResponse, AssetQueryParams, AssetFilterOptions } from '../../../services/assetService';
import { usePermissions } from '../../../hooks/usePermissions';
import AssetsTable from '../components/AssetsTable';
import AssetModal from '../components/AssetModal';
import AssetFilters from '../components/AssetFilters';
import AssetHistoryModal from '../components/AssetHistoryModal';
import ServerPagination from '../../../components/ServerPagination';

const PAGE_SIZE_OPTIONS = [20, 50, 100];
const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 20;

const AssetsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showModal, setShowModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [historyAsset, setHistoryAsset] = useState<Asset | null>(null);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Applied filters (what's actually sent to server)
  const [filters, setFilters] = useState({
    serviceAreaId: '',
    contractAreaId: '',
    assetType: '',
    search: ''
  });

  // Search state (separate from filters)
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Pagination state (init from URL in useEffect)
  const [pagination, setPagination] = useState({
    page: DEFAULT_PAGE,
    limit: DEFAULT_LIMIT,
    totalCount: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false
  });

  // Server-side sort (allowlisted server-side)
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Add filter options state for server-side filtering
  const [filterOptions, setFilterOptions] = useState<AssetFilterOptions>({
    serviceAreas: [],
    contractAreas: [],
    assetTypes: []
  });

  const { hasPermission } = usePermissions();

  const updateUrlParams = (newPagination?: Partial<{ page: number; limit: number }>, newFilters?: typeof filters) => {
    const params = new URLSearchParams(searchParams);
    if (newPagination !== undefined) {
      if (newPagination.page !== undefined) params.set('page', newPagination.page.toString());
      if (newPagination.limit !== undefined) params.set('limit', newPagination.limit.toString());
    }
    if (newFilters !== undefined) {
      const keys: (keyof typeof filters)[] = ['serviceAreaId', 'contractAreaId', 'assetType', 'search'];
      keys.forEach(key => {
        if (newFilters[key]) params.set(key, newFilters[key]);
        else params.delete(key);
      });
    }
    setSearchParams(params);
  };

  // Initialize state from URL (and when user navigates back/forward)
  useEffect(() => {
    const pageFromUrl = parseInt(searchParams.get('page') || String(DEFAULT_PAGE), 10);
    const limitFromUrl = parseInt(searchParams.get('limit') || String(DEFAULT_LIMIT), 10);
    const page = pageFromUrl >= 1 ? pageFromUrl : DEFAULT_PAGE;
    const limit = PAGE_SIZE_OPTIONS.includes(limitFromUrl) ? limitFromUrl : DEFAULT_LIMIT;
    setPagination(prev => ({ ...prev, page, limit }));
    setFilters({
      serviceAreaId: searchParams.get('serviceAreaId') || '',
      contractAreaId: searchParams.get('contractAreaId') || '',
      assetType: searchParams.get('assetType') || '',
      search: searchParams.get('search') || ''
    });
    setSearchTerm(searchParams.get('search') || '');
    setSearchInput(searchParams.get('search') || '');
  }, [searchParams]);

  const fetchAssets = async (resetPage: boolean = false) => {
    try {
      setLoading(true);
      setError(null);
      
      // Use current pagination page or reset to 1
      const currentPage = resetPage ? 1 : pagination.page;
      
      // Build query parameters
      const queryParams: AssetQueryParams = {
        page: currentPage,
        limit: pagination.limit,
        ...(filters.serviceAreaId && { serviceAreaId: parseInt(filters.serviceAreaId) }),
        ...(filters.contractAreaId && { contractAreaId: parseInt(filters.contractAreaId) }),
        ...(filters.assetType && { assetType: filters.assetType }),
        ...(searchTerm && { search: searchTerm }),
        ...(sortBy && { sortBy, sortOrder: sortDir })
      };

      console.log('AssetsPage: Fetching assets with params:', queryParams);

      const response = await assetService.getAssetsPaginated(queryParams);
      console.log('AssetsPage: Successfully fetched assets:', response.data?.length || 0, 'items');
      
      setAssets(response.data);
      setPagination({
        page: response.pagination.page,
        limit: response.pagination.limit,
        totalCount: response.pagination.totalCount,
        totalPages: response.pagination.totalPages,
        hasNextPage: response.pagination.hasNextPage,
        hasPreviousPage: response.pagination.hasPreviousPage
      });
      
      // If we reset the page, update the pagination page
      if (resetPage) {
        setPagination(prev => ({ ...prev, page: 1 }));
      }
    } catch (error) {
      console.error('AssetsPage: Error loading assets:', error);
      setError(error instanceof Error ? error.message : 'Failed to load assets. Please try again later.');
      setAssets([]);
      setPagination(prev => ({ ...prev, totalCount: 0, totalPages: 0 }));
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      console.log('AssetsPage: Fetching filter options...');
      const options = await assetService.getFilterOptions();
      console.log('AssetsPage: Successfully fetched filter options:', options);
      setFilterOptions(options);
    } catch (error) {
      console.error('AssetsPage: Error fetching filter options:', error);
    }
  };

  // Add pagination handlers
  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
    updateUrlParams({ page: newPage });
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPagination(prev => ({
      ...prev,
      limit: newPageSize,
      page: 1
    }));
    updateUrlParams({ limit: newPageSize, page: 1 });
  };

  const handleSort = (key: string, dir: 'asc' | 'desc') => {
    setSortBy(key);
    setSortDir(dir);
  };

  // Search handlers (following forms pattern)
  const toggleSearch = () => {
    setIsSearchVisible(!isSearchVisible);
    if (isSearchVisible) {
      setSearchTerm(''); // Clear search when hiding
      setSearchInput(''); // Clear input when hiding
    }
  };

  const handleSearch = () => {
    const trimmed = searchInput.trim();
    setSearchTerm(trimmed);
    updateUrlParams({ page: 1 }, { ...filters, search: trimmed });
  };

  const handleSearchInputChange = (value: string) => {
    setSearchInput(value);
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    updateUrlParams({ page: 1 }, { ...filters, search: '' });
  };

  // Update useEffect to refetch when pagination changes
  useEffect(() => {
    fetchAssets();
  }, [pagination.page, pagination.limit]);

  // Handle filter/sort changes (resets to page 1)
  useEffect(() => {
    fetchAssets(true); // Reset page when filters or sort change
  }, [filters.serviceAreaId, filters.contractAreaId, filters.assetType, searchTerm, sortBy, sortDir]);

  // Initial data loading
  useEffect(() => {
    fetchFilterOptions();
  }, []);

  const handleAddAsset = () => {
    setSelectedAsset(null);
    setShowModal(true);
  };

  const handleRowClick = (asset: Asset) => {
    setSelectedAsset(asset);
    setShowModal(true);
  };

  const handleShowHistory = (asset: Asset) => {
    setHistoryAsset(asset);
  };

  const handleSaveAsset = async (assetData: any) => {
    try {
      if (selectedAsset) {
        // Update existing asset
        await assetService.updateAsset(selectedAsset.id, assetData);
      } else {
        // Create new asset
        await assetService.createAsset(assetData);
      }
      await fetchAssets();
      setShowModal(false);
    } catch (err) {
      console.error('Error saving asset:', err);
      // You might want to show an error message to the user here
    }
  };

  const handleDeleteAsset = async (assetId: number) => {
    if (window.confirm('Are you sure you want to delete this asset?')) {
      try {
        await assetService.deleteAsset(assetId);
        await fetchAssets();
        setShowModal(false);
      } catch (err) {
        console.error('Error deleting asset:', err);
        // You might want to show an error message to the user here
      }
    }
  };

  const handleToggleStatus = async (assetId: number) => {
    try {
      await assetService.toggleAssetStatus(assetId);
      await fetchAssets();
    } catch (err) {
      console.error('Error toggling asset status:', err);
      // You might want to show an error message to the user here
    }
  };



  const handleApplyFilters = (newFilters: typeof filters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 1 }));
    updateUrlParams({ page: 1 }, newFilters);
  };

  const handleClearFilters = () => {
    const emptyFilters = {
      serviceAreaId: '',
      contractAreaId: '',
      assetType: '',
      search: ''
    };
    setFilters(emptyFilters);
    setPagination(prev => ({ ...prev, page: 1 }));
    setSearchTerm('');
    setSearchInput('');
    updateUrlParams({ page: 1 }, emptyFilters);
  };

  if (loading) {
    return <div>Loading assets...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

      return (
      <div className="assets-page tw-w-full tw-p-4 tw-flex tw-flex-col tw-h-full tw-overflow-hidden">
      <PageHeader
        title="Assets"
        actions={[
          {
            key: 'search',
            label: `Search${searchTerm ? ` (${searchTerm.length > 20 ? searchTerm.substring(0, 20) + '…' : searchTerm})` : ''}`,
            icon: isSearchVisible ? 'bi-x-lg' : 'bi-search',
            intent: 'secondary',
            active: isSearchVisible || !!searchTerm,
            title: isSearchVisible ? 'Close search' : 'Search assets',
            onClick: toggleSearch,
          },
          {
            key: 'add',
            label: 'Add Asset',
            icon: 'bi-plus-lg',
            intent: 'primary',
            hidden: !hasPermission('asset:create'),
            onClick: handleAddAsset,
          },
        ]}
      />

      <div className="tw-shrink-0">
      <AssetFilters
        filterOptions={filterOptions}
        filters={filters}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
      />
      </div>

      {/* Search field appears below filters when visible */}
      {isSearchVisible && (
        <Row className="tw-mb-3 tw-shrink-0">
          <Col xs={12}>
            <InputGroup className="tw-shadow-sm">
              <InputGroup.Text className="tw-bg-primary tw-text-white tw-border-primary">
                <i className="bi bi-search"></i>
              </InputGroup.Text>
              <Form.Control
                type="text"
                className="tw-border-primary/40"
                placeholder="Search by asset name, asset number, or description..."
                value={searchInput}
                onChange={(e) => handleSearchInputChange(e.target.value)}
                onKeyDown={(e) => handleSearchKeyPress(e)}
                autoFocus
              />
              <Button
                variant="primary"
                onClick={handleSearch}
                title="Search"
              >
                <i className="bi bi-search"></i>
              </Button>
              {(searchInput || searchTerm) && (
                <Button
                  variant="outline-primary"
                  onClick={clearSearch}
                  title="Clear search"
                >
                  <i className="bi bi-x-lg"></i>
                </Button>
              )}
            </InputGroup>
            {searchTerm && (
              <div className="tw-text-content-muted tw-text-sm tw-mt-1">
                Searching for: "{searchTerm}"
              </div>
            )}
          </Col>
        </Row>
      )}
      
      <div className="tw-grow table-scroll-wrapper tw-overflow-auto" style={{ minHeight: 0 }}>
      <AssetsTable
        assets={assets}
        onRowClick={handleRowClick}
        onShowHistory={handleShowHistory}
        onDelete={hasPermission('asset:delete') ? handleDeleteAsset : undefined}
        onToggleStatus={hasPermission('asset:update') ? handleToggleStatus : undefined}
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={handleSort}
      />
      </div>
      
      <div className="tw-shrink-0">
      <ServerPagination
        currentPage={pagination.page}
        totalPages={pagination.totalPages}
        totalCount={pagination.totalCount}
        pageSize={pagination.limit}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        loading={loading}
        className="tw-mt-4"
      />
      </div>
      
      <AssetModal 
        show={showModal} 
        onHide={() => setShowModal(false)}
        asset={selectedAsset}
        onSave={handleSaveAsset}
        filterOptions={filterOptions}
      />

      {historyAsset && (
        <AssetHistoryModal
          visible={!!historyAsset}
          onClose={() => setHistoryAsset(null)}
          assetId={historyAsset.id}
          assetName={historyAsset.name}
        />
      )}
    </div>
  );
};

export default AssetsPage;